import { Box, Card, Divider, Typography, Grid } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BadgePlus } from "lucide-react";

import { Alert, Button, TextField, UserDropdown, Autocomplete } from "../../../../shared";

// No step components reused

import useCreateQuickProjectHandler from "./hooks/useCreateQuickProject";
import { newProjectV2Validation } from "../../../../libs/form-validations";

const defaultValues = {
    name: "",
    description: "",
    type: "Residential",
    salesmanName: null,
    secondarySalesmanName: "",
    hasSecondarySalesman: false,
    leadSource: null,
    secondaryLead: "",
    customerName: "",
    customerPhoneNumber: "",
    customerEmail: "",
    customerStreetAddress: "",
    customerCity: "",
    customerState: "",
    customerZipCode: "",
    customerCountry: "Denver County",
};

export default function NewProjectV2() {
    const navigate = useNavigate();
    const { createProjectEventHandler, progress, useInitializeForm } = useCreateQuickProjectHandler();

    const methods = useForm({
        mode: "onChange",
        defaultValues,
        resolver: newProjectV2Validation,
    });

    useInitializeForm(methods.setValue, methods.getValues);

    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const onSubmit = async (data: any) => {
        const { projectId } = await createProjectEventHandler(data);
        if (projectId) {
            navigate(`/project/list/${projectId}`);
        }
    };

    return (
        <Box m={2} mx={3} width={800}>
            <Box display={"flex"} gap={0.8} alignItems={"center"} mb={1}>
                <Card sx={{ p: 0.5, borderRadius: "6px" }}>
                    <BadgePlus size={14} />
                </Card>
                <Typography level="title-lg">New Project (Quick Create)</Typography>
            </Box>
            <Typography level="body-sm">
                Provide basic project and customer information to quickly create a new project.
            </Typography>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box mt={2}>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Project Name"}
                                    helperText={"Name of the project"}
                                    placeholder={"My Villa Roofing"}
                                    name={"name"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <Autocomplete
                                    options={["Residential", "Commercial", "Industrial"]}
                                    label={"Project Type"}
                                    placeholder={"Residential"}
                                    helperText={"Type of project"}
                                    name={"type"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <UserDropdown
                                    label={"Salesman Name"}
                                    placeholder={"Select salesman"}
                                    helperText={"Assigned sales representative"}
                                    name={"salesmanName"}
                                />
                            </Grid>
                            {/* Customer Details */}
                            <Grid xs={12} mt={2}>
                                <Divider />
                            </Grid>
                            
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Customer Name"}
                                    helperText={"Name of the Customer"}
                                    placeholder={"Jane Doe"}
                                    name={"customerName"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Phone Number"}
                                    helperText={"Contact number"}
                                    startDecorator={"+1"}
                                    placeholder={"9123456789"}
                                    name={"customerPhoneNumber"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Email"}
                                    helperText={"Email"}
                                    placeholder={"jane.doe@roofinmotion.com"}
                                    name={"customerEmail"}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <TextField
                                    label={"Customer Street Address"}
                                    helperText={"Street name and house/building number of the customer"}
                                    placeholder={"123 Main St"}
                                    name={"customerStreetAddress"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Customer City"}
                                    helperText={"City where the customer is located"}
                                    placeholder={"Denver"}
                                    name={"customerCity"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Customer State"}
                                    helperText={"State where the customer is located"}
                                    placeholder={"CO"}
                                    name={"customerState"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Customer ZIP Code"}
                                    helperText={"5-digit ZIP code of the customer"}
                                    placeholder={"80202"}
                                    name={"customerZipCode"}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <TextField
                                    label={"Customer County"}
                                    helperText={"County where the customer is located"}
                                    placeholder={"Denver County"}
                                    name={"customerCountry"}
                                />
                            </Grid>
                        </Grid>
                        <Divider sx={{ mt: 3 }} />
                    </Box>

                    {/* Navigation Buttons */}
                    <Box mt={3} display="flex" alignItems="center" gap={1} pb={3}>
                        <Button type="submit" loading={progress.loading} loadingPosition="end" disabled={!isValid}>
                            {progress.loading ? "Creating..." : "Create Project"}
                        </Button>
                    </Box>

                    {progress.loading && progress.message && (
                        <Typography sx={{ mt: 2 }} level="title-sm">
                            {progress.message}
                        </Typography>
                    )}

                    {progress.error && (
                        <Box mt={2}>
                            <Alert errorMessage={progress.error} />
                        </Box>
                    )}
                </form>
            </FormProvider>
        </Box>
    );
}
